import { useMemo } from "react"
import classNames from "classnames"

import { useGetPerformanceTestWidgetDataQuery } from "store/api/performanceTestApi"
import { useAppSelector } from "store/hooks"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { BarChart } from "components/common/BarChart"
import { Spinner } from "components/common/Spinner"
import { dummyPerfTestWidgetData } from "pages/contractor/PerformanceTestPage/dummyPerfTestData"

import styles from "./index.module.scss"

export const WidgetPerformanceTest = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasPerformanceTestAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.PerformanceTest)
    : false
  const { data: widgetData, isFetching } = useGetPerformanceTestWidgetDataQuery(undefined, {
    skip: !hasPerformanceTestAccess,
  })

  const displayData = useMemo(() => {
    if (hasPerformanceTestAccess) {
      return widgetData
        ? widgetData
        : {
            scheduled: 0,
            rescheduled: 0,
            running: 0,
            passed: 0,
            issue: 0,
          }
    }
    return dummyPerfTestWidgetData
  }, [widgetData, hasPerformanceTestAccess])

  const hasNoData =
    displayData.scheduled + displayData.rescheduled + displayData.running + displayData.passed + displayData.issue === 0

  return (
    <div className={classNames(styles.perfTestWidgetWrapper)}>
      <header className={styles.widgetHeader}>
        <h3 className={styles.widgetTitle}>Performance Test Statuses</h3>
      </header>
      <div className={styles.chartWrapper}>
        <BarChart
          showEmptyState
          items={[
            {
              color: "#43E0F8",
              value: displayData.scheduled,
            },
            {
              color: "#3b82f6",
              value: displayData.rescheduled,
            },
            {
              color: "#f59e0b",
              value: displayData.running,
            },
            {
              color: "#22C55E",
              value: displayData.passed,
            },
            {
              color: "#EF4444",
              value: displayData.issue,
            },
          ]}
        />
        {!hasNoData && (
          <div className={styles.indicatorsWrapper}>
            <span className={styles.indicator}>
              <i
                className={styles.colorCircle}
                style={{
                  backgroundColor: "#43E0F8",
                }}
              />
              Scheduled
              <i>{displayData.scheduled}</i>
            </span>
            <span className={styles.indicator}>
              <i
                className={styles.colorCircle}
                style={{
                  backgroundColor: "#3B82F6",
                }}
              />
              Rescheduled
              <i>{displayData.rescheduled}</i>
            </span>
            <span className={styles.indicator}>
              <i
                className={styles.colorCircle}
                style={{
                  backgroundColor: "#f59e0b",
                }}
              />
              Running
              <i>{displayData.running}</i>
            </span>
            <span className={styles.indicator}>
              <i
                className={styles.colorCircle}
                style={{
                  backgroundColor: "#22C55E",
                }}
              />
              Passed
              <i>{displayData.passed}</i>
            </span>
            <span className={styles.indicator}>
              <i
                className={styles.colorCircle}
                style={{
                  backgroundColor: "#EF4444",
                }}
              />
              Issue
              <i>{displayData.issue}</i>
            </span>
          </div>
        )}
        {isFetching && (
          <div className={styles.widgetLoader}>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  )
}
